import { DeleteOutline } from '@mui/icons-material';
import { Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../AddMoreButton';
import FormButtonOptionsInput from '../FormInputs/FormButtonOptionsInput';
import ToggleInput from '../FormInputs/ToggleInput';
import StaffInfoInputs from './StaffInfoInputs';
import StaffInfoModal from './StaffInfoModal';

FormStaffInfoFieldWithButtons.propTypes = {
  fieldName: PropTypes.string,
  focusedField: PropTypes.string,
  setFocusedField: PropTypes.func,
  fieldAttributes: PropTypes.object,
};

export default function FormStaffInfoFieldWithButtons({ fieldName, focusedField, setFocusedField, fieldAttributes }) {
  const { control, watch, getValues, setValue } = useFormContext();
  const [isEditable, setIsEditable] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const isCanceled = useSelector((state) => state.transitions?.data?.isCanceled);
  const hasInitialized = useRef(false);

  const { append, update } = useFieldArray({
    keyName: '_id',
    control,
    name: `${fieldName}.staffMembers`,
  });

  const isVacant = watch(`${fieldName}.isVacant`);
  const staffFields = watch(`${fieldName}.staffMembers`)
    ?.map((item, originalIndex) => ({ ...item, originalIndex }))
    ?.filter((x) => !x.deleteFlag);

  const newStaffMember = {
    firstName: null,
    lastName: null,
    cellPhone: null,
    email: null,
    staffSubTypeId: null,
    id: 0,
    deleteFlag: false,
  };

  useEffect(() => {
    if (fieldAttributes.isEditable === false) {
      setIsEditable(false);
    } else {
      setIsEditable(!isVacant);
    }

    const fieldNames = ['firstName', 'lastName', 'cellPhone', 'email', 'staffSubTypeId'];
    const hasFieldsFilled = staffFields?.some((field) =>
      fieldNames.some((x) => !!getValues(`${fieldName}.staffMembers[${field.originalIndex}].${x}`))
    );

    // Skip automatic append until form data has been loaded (isVacant changes from undefined to a boolean)
    // This prevents marking the form as dirty during initial data load
    if (isVacant !== undefined && !hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    if (!hasInitialized.current) {
      return;
    }

    if (isVacant && hasFieldsFilled) setOpenModal(true);
    else if (isVacant) onProceed();
  }, [isVacant]);

  const onProceed = () => {
    staffFields?.forEach((field) => {
      setValue(`${fieldName}.staffMembers[${field.originalIndex}].deleteFlag`, true);
    });
    append(newStaffMember);
    setOpenModal(false);
  };

  const onClose = () => {
    setValue(`${fieldName}.isVacant`, false);
    setValue(`${fieldName}.isDiffDept`, false);
    setOpenModal(false);
  };

  return (
    <Grid>
      <Grid container>
        <Grid item>
          <Typography variant="subtitle1" color="text.secondary">
            {fieldAttributes.questionTitle}
          </Typography>
        </Grid>
        <Grid item sx={{ ml: 'auto', mr: 1 }}>
          <Controller
            name={`${fieldName}.isVacant`}
            control={control}
            render={() => (
              <ToggleInput
                fieldAttributes={{ ...fieldAttributes, label: 'Vacant' }}
                fieldName={`${fieldName}.isVacant`}
              />
            )}
          />
        </Grid>
      </Grid>
      {staffFields?.map((field) => (
        <Grid
          container
          key={field.originalIndex}
          alignItems="center"
          sx={{
            mb: 1,
            mt: 2,
            p: 2,
            borderRadius: '4px',
            border: '2px solid rgba(145, 158, 171, 0.24)',
          }}
        >
          <Grid item xs={staffFields?.length > 1 ? 11.5 : 12}>
            <StaffInfoInputs
              fieldName={`${fieldName}.staffMembers[${field.originalIndex}]`}
              focusedField={focusedField}
              setFocusedField={setFocusedField}
              fieldAttributes={fieldAttributes}
              isEditable={isEditable}
            />
          </Grid>
          {staffFields?.length > 1 && (
            <Grid item xs={0.5}>
              <IconButton
                onClick={() => {
                  update(field.originalIndex, { ...field, deleteFlag: true });
                }}
                disabled={!isEditable || isCanceled}
                color="error"
              >
                <DeleteOutline />
              </IconButton>
            </Grid>
          )}
          <Controller
            name={`${fieldName}.staffMembers[${field.originalIndex}].staffSubTypeId`}
            control={control}
            render={() => (
              <FormButtonOptionsInput
                fieldName={`${fieldName}.staffMembers[${field.originalIndex}].staffSubTypeId`}
                fieldAttributes={{ ...fieldAttributes, isEditable }}
              />
            )}
          />
        </Grid>
      ))}
      <AddMoreButton
        buttonLabel="Add more"
        handleAddMoreClick={() => {
          append(newStaffMember);
        }}
        disabled={!isEditable}
      />
      <StaffInfoModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        onProceed={onProceed}
        onClose={onClose}
        fieldAttributes={fieldAttributes}
        label="vacant"
        staffSubType
      />
    </Grid>
  );
}
